import React, { useState, useEffect, useRef } from 'react';
import { Box, Drawer, Menu, MenuItem } from '@mui/material';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logoutUser, forceLogout } from '../store/slices/authSlice';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;
const getIdleTimeout = () => {
  const stored = localStorage.getItem('sessionTimeout');
  return stored ? Number(stored) * 60 * 1000 : 30 * 60 * 1000;
};

const DashboardLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const idleTimer = useRef(null);

  // Auto-logout on idle
  useEffect(() => {
    const resetTimer = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(async () => {
        try {
          // Clear local storage and force logout
          localStorage.removeItem('token');
          localStorage.removeItem('loginLockout');
          dispatch(forceLogout());
          navigate('/admin/login');
        } catch (error) {
          console.error('Idle logout error:', error);
          // Force logout even if there's an error
          dispatch(forceLogout());
          navigate('/admin/login');
        }
      }, getIdleTimeout());
    };
    
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];
    events.forEach(event => window.addEventListener(event, resetTimer));
    
    resetTimer();
    
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      events.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [dispatch, navigate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (page) => {
    setAnchorEl(null);
    if (page === 'profile') {
      navigate('/admin/profile');
    } else if (page === 'account-settings') {
      navigate('/admin/account-settings');
    }
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      setAnchorEl(null);
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if API call fails
      dispatch(forceLogout());
      setAnchorEl(null);
      navigate('/admin/login');
    }
  };

  const currentDrawerWidth = sidebarCollapsed ? collapsedDrawerWidth : drawerWidth;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      <Header
        getPageTitle={() => ''}
        currentPage={'main'}
        handleDrawerToggle={handleDrawerToggle}
        handleProfileMenuOpen={handleProfileMenuOpen}
      />
      <Box sx={{ display: 'flex', flex: 1 }}>
        <Box
          component="nav"
          sx={{ width: { sm: currentDrawerWidth }, flexShrink: { sm: 0 } }}
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}
          >
            <Sidebar
              sidebarCollapsed={sidebarCollapsed}
              handleSidebarToggle={handleSidebarToggle}
              navigate={navigate}
              location={location}
              currentPage={'main'}
            />
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: currentDrawerWidth, transition: 'width 0.2s ease-in-out' } }}
            open
          >
            <Sidebar
              sidebarCollapsed={sidebarCollapsed}
              handleSidebarToggle={handleSidebarToggle}
              navigate={navigate}
              location={location}
              currentPage={'main'}
            />
          </Drawer>
        </Box>
        <Box component="main" sx={{ flexGrow: 1, px: { xs: 0.5, sm: 1, md: 1 }, py: 3, width: { sm: `calc(100% - ${currentDrawerWidth}px)` }, mt: 8, transition: 'width 0.2s ease-in-out' }}>
          <Outlet />
        </Box>
      </Box>
      <Footer />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => handleMenuClick('profile')}>Profile</MenuItem>
        <MenuItem onClick={() => handleMenuClick('account-settings')}>Account Settings</MenuItem>
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>
    </Box>
  );
};

export default DashboardLayout; 